import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, input, output, untracked, viewChild } from '@angular/core';
import { Constants } from '@frontendlib/common/types/Constants';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { ARCHIVE_EMPTY_TITLE, NO_MATCH_TITLE, TRASH_EMPTY_TITLE } from '@modules/common/components/list/base-stitch-list.component';
import { ListItemSeparatorComponent } from '@modules/common/components/list/list-items-separator/list-items-separator.component';
import { ListSelectionBarComponent } from '@modules/common/components/list/list-selection-bar/list-selection-bar.component';
import { ListLoadErrorComponent } from '@modules/common/components/list/list-load-error/list-load-error.component';
import { Pagination2Component } from '@modules/common/components/pagination/pagination2.component';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { SelectableListDirective } from '@modules/drag-n-drop/directives/selectable-list.directive';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { type SelectableService } from '@modules/drag-n-drop/services/selectable.service';
import { type SelectableItem } from '@modules/drag-n-drop/types/selectable-item';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';
import { UtilsModule } from '@modules/utils/utils.module';
import { filter, skip, Subject, type Subscription } from 'rxjs';
import { BoardsFoldersListContextMenuDirective } from '../../menus/folders-list-context-menu.directive';
import { BoardsListContextMenuDirective } from '../../menus/boards-list-context-menu.directive';
import { BoardListStore } from '../../../services/board-list.store';
import { BoardsStore } from '../../../services/boards.store';
import { type Board, type BoardSidebarView } from '../../../types/board';
import { filterBoards, sortBoards, toListItems } from '../../shared/board-list.util';
import { BoardCardComponent } from '../board-card/board-card.component';
import { BoardsFoldersListComponent } from '../boards-folders-list/boards-folders-list.component';

/** How many renders a selection carried to renamed boards waits for their rows to register. */
const CARRY_RENDER_ATTEMPTS = 3;

/** A selection carried over to renamed boards, waiting for their rows to register. */
interface PendingCarry {
  /** The entries to re-apply, in selection order. */
  entries: SelectableItem<Board>[];
  /** Renders waited since the carry last changed. */
  renders: number;
  /** Watches the selection for the user taking entries out of it while the carry waits. */
  readonly watch: Subscription;
}

export const BOARD_VIEW_TITLES: Record<BoardSidebarView, string> = {
  all: 'All boards',
  recent: 'Recent',
  flagged: 'Flagged',
  followup: 'Follow Up',
  snoozed: 'Snoozed',
  templates: 'Templates',
  archived: 'Archive',
  deleted: 'Trash',
  search_results: 'All Results',
};

/**
 * A page of boards, laid out like the other apps' lists: section headings by day (or first letter when sorted by
 * title), pinned/flagged rows on top on the highlight ground, and the shared pagination footer. What it shows is
 * read from the host's `BoardListStore`, so the Boards page and the dynamic panel each keep their own view.
 */
@Component({
  selector: 'app-boards-list',
  templateUrl: './boards-list.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-full flex-col' },
  imports: [
    BoardCardComponent,
    ListItemSeparatorComponent,
    ListLoadErrorComponent,
    ListSelectionBarComponent,
    LoadingIndicatorComponent,
    Pagination2Component,
    NoContentStubComponent,
    ContextMenuStateIndicatorComponent,
    BoardsListContextMenuDirective,
    BoardsFoldersListContextMenuDirective,
    BoardsFoldersListComponent,
    UtilsModule,
    DragNDropModule,
  ],
})
export class BoardsListComponent {
  /** The board the host is previewing. The list does not draw it — the highlight is the list's own
   * selection (list-selection §1) — but a board selected from elsewhere (just created, restored on reload)
   * becomes the selected row once it is on the page, so the preview and the highlight agree. */
  public readonly selectedBoardId = input<string | null>(null);
  public readonly showHeader = input(true);
  /** True while the host split-view pane is collapsed to its icon-only rail — hides the header, filter
   * indicator, list and pagination footer entirely instead of rendering them at a 25px width they cannot
   * fit in, matching Notes' own list container (SBUX-988 review). The dynamic panel, which mounts this
   * component with no such collapsible pane around it, simply never passes it. */
  public readonly minimized = input(false);
  /** Whether the selection drives a preview beside the list (the Boards page). Such a list keeps a re-clicked
   * row selected; one that opens nothing on selection (the dynamic panel) deselects it — list-selection §4. */
  public readonly drivesPreview = input(true);
  /** The list beside an open editor (SBUX-1144's list toggle), where a selection opens its board in the editor. A board
   * leaving this list only leaves it: the selection does not move on to the next board or clear the store's, which
   * would switch the editor to another board or blank it. */
  public readonly besideEditor = input(false);

  /** An explicit "open" intent: the context menu's own "Open" action. Never fired by a click or the
   * selection: a host that swaps its whole list out for the board on `openBoard` — the dynamic panel — must not
   * do that while the user is building a multi-selection, or the very list the selection lives on vanishes
   * mid-gesture. */
  public readonly openBoard = output<Board>();
  public readonly doubleClickBoard = output<Board>();
  /** The selection settled on exactly one board with no modifier held — a plain click or an arrow key
   * (list-selection §5). A host with room for a preview alongside its list (the Boards page's split view)
   * shows it; modifier clicks, Shift+Arrow and Ctrl+A never emit, so building a multi-selection leaves the
   * preview alone. A host that would replace the list on open (the dynamic panel) leaves this unbound. */
  public readonly previewBoard = output<Board>();
  /** A folder picked on the "Folders" view (`BoardListStore.foldersOpen`); `selectAll` from its menu's "Select all". */
  public readonly openFolder = output<{ id: string; selectAll: boolean }>();
  /** A folder card's "New board" made this board in that folder. */
  public readonly folderBoardCreated = output<{ folderId: string; boardId: string }>();

  protected readonly store = inject(BoardsStore);
  protected readonly listStore = inject(BoardListStore);
  protected readonly TRASH_EMPTY_TITLE = TRASH_EMPTY_TITLE;
  protected readonly ARCHIVE_EMPTY_TITLE = ARCHIVE_EMPTY_TITLE;
  protected readonly NO_MATCH_TITLE = NO_MATCH_TITLE;
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly injector = inject(Injector);
  private readonly scrollableContainer = viewChild<ElementRef<HTMLElement>>('scrollableContainer');
  private readonly selectableList = viewChild(SelectableListDirective<Board>);

  protected readonly pageSize = Constants.PAGE_SIZE;

  /** Only true before the list has ever loaded successfully — like Mail's own list, a background
   * `refresh()` on an already-populated list must not blank it out behind a spinner (SBUX-988 review). */
  protected readonly loading = computed(
    // While the first read still streams in (R13-SCALE-3), a view with nothing to show yet keeps the loading state rather than saying "No boards".
    () => this.store.boardsLoading() && (!this.store.boardsLoaded() || (!this.store.boardsComplete() && this.items().length === 0)),
  );
  protected readonly loadError = computed(() => (this.store.boardsLoadError() ? true : undefined));

  protected readonly title = computed(() => {
    if (this.listStore.foldersOpen()) return 'Folders';
    const folderId = this.listStore.folderId();
    if (folderId) {
      return this.store.folders().find((folder) => folder.id === folderId)?.title ?? 'Folder';
    }
    return BOARD_VIEW_TITLES[this.listStore.view()];
  });

  private readonly boards = computed(() =>
    sortBoards(
      filterBoards(
        this.store.boards(),
        this.listStore.view(),
        this.listStore.folderId(),
        this.listStore.options(),
        this.listStore.effectiveSearch(),
        this.listStore.markers(),
        this.displayTimeZoneService.timeZone(),
        this.listStore.now(),
      ),
      this.listStore.options(),
    ),
  );

  protected readonly totalCount = computed(() => this.boards().length);

  protected readonly items = computed(() => {
    const page = this.listStore.page();
    const timeZone = this.displayTimeZoneService.timeZone();
    return toListItems(
      this.boards().slice((page - 1) * this.pageSize, page * this.pageSize),
      this.listStore.options(),
      (date) => toDisplayZoneWallClock(date, timeZone),
    );
  });

  /** Bound to `appSelectableListItems` so the list's `openSelected` is typed with the board. */
  protected readonly selectableRows = computed(() => this.items().map((item) => ({ data: item.board })));

  /** Whether the previewed board is on the current page. Its becoming true — the list's first rows, a page
   * round trip, a deep link to a later page — is what re-highlights it. */
  private readonly previewedOnPage = computed(() => {
    const id = this.selectedBoardId();
    return !!id && this.items().some((item) => item.board.id === id);
  });

  /** Everything `boards` is filtered by. A board that leaves `boards` while this stays the same left because
   * the board itself changed — trashed, archived, restored, moved, edited out of the view (list-selection §1). */
  private readonly filterKey = computed((): readonly unknown[] => [
    this.listStore.view(),
    this.listStore.folderId(),
    this.listStore.options(),
    this.listStore.effectiveSearch(),
    this.listStore.markers(),
    this.displayTimeZoneService.timeZone(),
    this.listStore.now(),
  ]);

  /** The board ids `boards` held at the last check, with the filter they were computed under. */
  private listed: { readonly key: readonly unknown[]; readonly ids: ReadonlySet<string> } | null = null;

  /** Fed to `appSelectableListReset` — a stale multi-selection references positions from whatever page/
   * view/folder/search/markers/filters was showing when it was made, so switching any of those clears it
   * rather than letting a leftover selection silently attach to whatever new board now sits at the same
   * position (or, with `options` left out, silently keep a board that a filter just hid — select A and B,
   * turn on Flagged leaving only A visible, then act on A through its context menu's "whole selection"
   * actions and have them reach the hidden B too). `markers()` and `effectiveSearch()` (not `search()`
   * alone) belong here for the same reason: a knot/tag/connection picked or cleared elsewhere, or an
   * inherited cross-app query changing with no local term typed here, narrows `filterBoards` exactly like
   * `options` does and can hide a selected board the same way (SBUX-988 review). */
  protected readonly resetSelected = new Subject<void>();

  /** The previewed board a Refresh un-highlighted. Refresh is an explicit reset (list-selection §1), so its
   * return to page 1 must not re-highlight it; a later page change, a change of view/folder/search/markers/
   * options or a different preview lifts this. */
  private refreshDismissedId: string | null = null;

  /** The selection carried over to renamed ids, waiting for its rows to register. One at a time: a rename landing
   * while it waits merges into it, so it cannot drop an entry an earlier rename carried. */
  private pendingCarry: PendingCarry | null = null;
  /** True while the carry itself sets the selection, so its watch does not read that as the user's change. */
  private applyingCarry = false;

  constructor() {
    effect(() => {
      const request = this.listStore.selectAllRequest();
      if (!request) return;
      afterNextRender(() => {
        const list = this.selectableList();
        if (list && this.listStore.consumeSelectAllRequest(request)) list.selectableService.triggerSelectAll();
      }, { injector: this.injector });
    });
    inject(DestroyRef).onDestroy(() => this.dropPendingCarry());
    effect(() => {
      const ids = this.items().map((item) => item.board.id);
      untracked(() => this.store.ensureListTags(ids));
    });
    // A page that emptied out (its last board trashed or moved) falls back to the last page that still has rows.
    effect(() => {
      const lastPage = Math.max(1, Math.ceil(this.totalCount() / this.pageSize));
      if (this.listStore.page() > lastPage) {
        this.refreshDismissedId = null;
        this.listStore.setPage(lastPage);
      }
    });

    effect(() => {
      this.listStore.page();
      this.listStore.view();
      this.listStore.folderId();
      this.listStore.effectiveSearch();
      this.listStore.markers();
      this.listStore.options();
      this.resetSelected.next();
    });

    // A board selected from outside the list becomes its selected row whenever it comes onto the page. Only
    // the previewed id, the list appearing and the board's presence on the page are tracked: a reload or a
    // gesture that leaves it where it is never re-runs this, so it cannot fight a multi-selection or a Clear.
    // Created after the reset above, so on a page change it runs after the reset has cleared the selection.
    // Not the page: Refresh itself returns to page 1, and that must not lift what it just dismissed. A preview
    // renamed from its temp id to its server id (its create resolved) is still the board Refresh dismissed.
    effect(() => {
      const id = this.selectedBoardId();
      untracked(() => {
        const dismissed = this.refreshDismissedId;
        if (dismissed === null || dismissed === id) return;
        this.refreshDismissedId = id !== null && this.store.resolveBoardId(dismissed) === id ? id : null;
      });
    });
    effect(() => {
      this.listStore.view();
      this.listStore.folderId();
      this.listStore.effectiveSearch();
      this.listStore.markers();
      this.listStore.options();
      untracked(() => (this.refreshDismissedId = null));
    });
    effect(() => {
      const id = this.selectedBoardId();
      const list = this.selectableList();
      if (!id || !list || !this.previewedOnPage()) return;
      untracked(() => {
        if (id === this.refreshDismissedId) return;
        const service = list.selectableService;
        // The store renames the previewed id along with the board, so this can run before the rename effect below
        // carries the selection over: an entry still on the temp id, or one a carry holds, already selects it. A
        // filter change in the same flush still carries that entry (`carryPreviewedOver`).
        const selected = service.getSelectedItemsSync();
        if (selected.some((item) => item.identifier === id || this.store.resolveBoardId(item.identifier) === id)) return;
        if (this.pendingCarry?.entries.some((item) => item.identifier === id)) return;
        const position = this.items().findIndex((item) => item.board.id === id);
        service.setSelectedItems([{ data: this.items()[position].board, position, identifier: id }]);
      });
    });

    // Boards that left the list because they changed are announced before the rows re-render, so a selection
    // made of them moves on to the next board and previews it (list-selection §1, §5.3 `removal`). A board whose
    // create just resolved did not leave: its row was renamed from the temp id to the server id, so its
    // selected entry is carried over to the new id instead.
    effect(() => {
      const key = this.filterKey();
      const ids = new Set(this.boards().map((board) => board.id));
      const list = this.selectableList();
      untracked(() => {
        const previous = this.listed;
        this.listed = { key, ids };
        if (!previous || previous.key !== key) {
          this.dropPendingCarry();
          if (previous && list) this.carryPreviewedOver(list);
          return;
        }
        const vanished = [...previous.ids].filter((id) => !ids.has(id));
        const renamed = new Map(
          vanished.map((id) => [id, this.store.resolveBoardId(id)] as const).filter(([id, current]) => current !== id && ids.has(current)),
        );
        if (renamed.size && list) this.carrySelectionOver(list, renamed);
        const gone = vanished.filter((id) => !renamed.has(id));
        // Beside the editor a removal is not announced, so the selection just drops, and an emptied page leaves the
        // editor's board selected.
        if (!gone.length || this.besideEditor()) return;
        list?.selectableService.expectRemoval(gone);
        const previewed = this.selectedBoardId();
        // An emptied page takes the list (and its selection) away with it; the preview must not stay on a board that left.
        if (previewed && gone.includes(previewed) && this.items().length === 0) {
          this.store.selectBoard(null);
        }
      });
    });

    // The selection advanced with nothing left to take it: the previewed board left with it.
    effect((onCleanup) => {
      const list = this.selectableList();
      if (!list) return;
      const subscription = list.selectableService
        .getAdvanced()
        .pipe(filter((target) => target === null))
        .subscribe(() => {
          const previewed = this.selectedBoardId();
          if (previewed && !this.boards().some((board) => board.id === previewed)) {
            this.store.selectBoard(null);
          }
        });
      onCleanup(() => subscription.unsubscribe());
    });

    // A folder deleted while it is open leaves the list on the default view, not on a folder that is gone
    // — but only once `folders` has actually finished its first load: right after a page reload it starts
    // empty and populates asynchronously, and without the `foldersLoaded` guard this fired on that empty
    // array before the real data ever arrived, resetting a persisted folder selection on every reload
    // regardless of whether the folder still existed.
    effect(() => {
      const folderId = this.listStore.folderId();
      if (folderId && this.store.foldersLoaded() && !this.store.folders().some((folder) => folder.id === folderId)) {
        this.listStore.setView('all');
      }
    });
  }

  protected handleOpenSelected(event: SelectableOpenEvent<Board>): void {
    // Re-selecting the board already previewed (after a Refresh or Escape cleared the highlight) must not
    // re-open it: that would send the preview back to its first slide.
    if (event.item.data.id === this.selectedBoardId()) return;
    this.previewBoard.emit(event.item.data);
  }

  protected changePage(page: number): void {
    this.refreshDismissedId = null;
    this.listStore.setPage(page);
    this.scrollToTop();
  }

  /** Re-fetches boards/folders from the backend (picks up a change made from another tab or session, or
   * recovers from a previous failed load), re-evaluates the "Recent" window (a board can age out of the
   * last 7 days between one refresh and the next), and returns to the top, page 1. An explicit reset, so it
   * clears the selection even when the list is already on page 1 (list-selection §1). */
  protected refresh(): void {
    this.refreshDismissedId = this.selectedBoardId();
    this.store.loadAll();
    this.listStore.refreshNow();
    this.listStore.setPage(1);
    this.resetSelected.next();
    this.scrollToTop();
  }

  /** A plain click on a row writes a one-row selection, which replaces whatever a carry is waiting to re-apply.
   * The selection alone cannot always tell: a re-click on the sole selected row keeps it without emitting. The
   * list ignores a click on a row it has not registered yet (a renamed row, for the pass before it registers),
   * so the carry then waits for that row alone, as the selection the click meant to write, and the row is opened
   * as the click would have opened it. */
  protected handleRowClick(event: MouseEvent, id: string): void {
    // The second click of a double-click (`detail` 2) belongs to the first, which has already opened the row (main #2722, as the Slides list).
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.detail > 1) return;
    const carry = this.pendingCarry;
    const list = this.selectableList();
    const entry = this.entryOnPage(id);
    if (!carry || !list || !entry || list.selectableService.items.value.some((row) => row.identifier === id)) {
      this.dropPendingCarry();
      return;
    }
    carry.entries = [entry];
    carry.renders = 0;
    this.setCarriedSelection(list.selectableService, [entry]);
    list.requestOpen(entry, 'pointer');
  }

  /** A filter change abandons carrying the selection over, but not the highlight on the previewed board: when the
   * highlight effect left the previewed board's entry, still on its temp id, for the carry to re-key, it is carried
   * on its own. */
  private carryPreviewedOver(list: SelectableListDirective<Board>): void {
    const id = this.selectedBoardId();
    if (!id || id === this.refreshDismissedId) return;
    const from = list.selectableService
      .getSelectedItemsSync()
      .find((item) => item.identifier !== id && this.store.resolveBoardId(item.identifier) === id)?.identifier;
    if (from !== undefined) this.carrySelectionOver(list, new Map([[from, id]]));
  }

  /** Re-keys selected entries whose board changed id (temp id → server id) so the row stays selected. The
   * renamed row is swapped for a new one, and the list reconciles its selection against the rows in between
   * — when the old row is gone and the new one not yet registered — dropping the entry; so the carried
   * entries are kept pending and re-applied once their rows have registered, unless something else replaced
   * the selection in the meantime or the user took entries out. A rename landing while a carry is
   * pending extends that carry, and waits afresh, instead of starting from the selection the list has trimmed. */
  private carrySelectionOver(list: SelectableListDirective<Board>, renamed: ReadonlyMap<string, string>): void {
    const service = list.selectableService;
    const selected = service.getSelectedItemsSync();
    const pending = this.pendingCarry;
    const base = pending ? this.withPendingCarry(pending.entries, selected) : selected;
    if (!base.some((item) => renamed.has(item.identifier))) return;
    const carried = base.flatMap((item) => {
      const id = renamed.get(item.identifier);
      if (id === undefined) return [item];
      const entry = this.entryOnPage(id);
      return entry ? [entry] : [];
    });
    this.setCarriedSelection(service, carried);
    if (!carried.length) {
      this.dropPendingCarry();
      return;
    }
    if (pending) {
      pending.entries = carried;
      pending.renders = 0;
      return;
    }
    const carry: PendingCarry = { entries: carried, renders: 0, watch: this.watchUserDeselection(service) };
    this.pendingCarry = carry;
    this.applyPendingCarryAfterRender(list, carry);
  }

  /** The pending carry in its order — each entry as currently selected, or as carried when the list dropped
   * it — followed by anything selected since. Entries whose board left the page are dropped. */
  private withPendingCarry(pending: readonly SelectableItem<Board>[], selected: readonly SelectableItem<Board>[]): SelectableItem<Board>[] {
    const selectedById = new Map(selected.map((item) => [item.identifier, item]));
    const pendingIds = new Set(pending.map((item) => item.identifier));
    const kept = pending.flatMap((item) => {
      const entry = selectedById.get(item.identifier) ?? this.entryOnPage(item.identifier);
      return entry ? [entry] : [];
    });
    return [...kept, ...selected.filter((item) => !pendingIds.has(item.identifier))];
  }

  /** Tells the user's deselection from the list's own trimming while a carry waits: the list only drops entries
   * whose row is not registered, so an entry dropped while its row is registered was deselected by the user and
   * leaves the carry. An emptied selection, or one cut down to a single row, abandons the carry — that is what a
   * plain click writes, and the selection it replaced must not come back (Ctrl-deselecting down to one row looks
   * the same, so it abandons the carry too). So does one row selected from an empty selection: the list trims
   * nothing from that, so it is a write (a plain arrow key or click), even onto a carried row. Rows the user only
   * added to a non-empty selection keep it. */
  private watchUserDeselection(service: SelectableService<Board>): Subscription {
    let seen = service.getSelectedItemsSync();
    return service
      .getSelectedItems()
      .pipe(skip(1))
      .subscribe(() => {
        // Read the selection itself, not the emission: a selection set from inside another subscriber reaches
        // this one before the emission it interrupted, which then arrives stale.
        const selected = service.getSelectedItemsSync();
        const previous = seen;
        seen = selected;
        const carry = this.pendingCarry;
        if (!carry || this.applyingCarry || selected === previous) return;
        const selectedIds = new Set(selected.map((item) => item.identifier));
        const registered = new Set(service.items.value.map((row) => row.identifier));
        const deselected = new Set(
          previous.filter((item) => !selectedIds.has(item.identifier) && registered.has(item.identifier)).map((item) => item.identifier),
        );
        if (previous.length === 0 && selected.length <= 1) {
          this.dropPendingCarry();
          return;
        }
        if (!deselected.size) return;
        if (selected.length <= 1) {
          this.dropPendingCarry();
          return;
        }
        carry.entries = carry.entries.filter((item) => !deselected.has(item.identifier));
        if (!carry.entries.length) this.dropPendingCarry();
      });
  }

  /** Re-applies the pending carry's entries whose rows have registered, followed by any rows the user added, and
   * keeps waiting while a carried row that is still on the page has not. A non-empty selection holding none of the
   * carried entries replaced them (a plain click, the list advancing past boards that left), so the carry gives way
   * to it. The list registers rows one change-detection pass after it renders them, so a single render can come
   * too early; the wait is bounded, as each scheduled render hook requests a render. */
  private applyPendingCarryAfterRender(list: SelectableListDirective<Board>, carry: PendingCarry): void {
    afterNextRender(
      () => {
        if (this.pendingCarry !== carry) return;
        const service = list.selectableService;
        const carriedIds = new Set(carry.entries.map((item) => item.identifier));
        const current = service.getSelectedItemsSync();
        const added = current.filter((item) => !carriedIds.has(item.identifier));
        if (added.length && added.length === current.length) {
          this.dropPendingCarry();
          return;
        }
        const currentById = new Map(current.map((item) => [item.identifier, item]));
        const rendered = new Set(service.items.value.map((row) => row.identifier));
        const onPage = new Set(this.items().map((item) => item.board.id));
        const waiting = carry.entries.some((item) => !rendered.has(item.identifier) && onPage.has(item.identifier));
        if (carry.entries.some((item) => !currentById.has(item.identifier) && rendered.has(item.identifier))) {
          const restored = carry.entries.flatMap((item) => {
            const entry = currentById.get(item.identifier) ?? (rendered.has(item.identifier) ? this.entryOnPage(item.identifier) : null);
            return entry ? [entry] : [];
          });
          this.setCarriedSelection(service, [...restored, ...added]);
        }
        carry.renders += 1;
        if (!waiting || carry.renders >= CARRY_RENDER_ATTEMPTS) {
          this.dropPendingCarry();
          return;
        }
        this.applyPendingCarryAfterRender(list, carry);
      },
      { injector: this.injector },
    );
  }

  /** The selection entry for a board at the position its row has on the current page, or `null` when it is not on it. */
  private entryOnPage(id: string): SelectableItem<Board> | null {
    const items = this.items();
    const position = items.findIndex((row) => row.board.id === id);
    return position === -1 ? null : { data: items[position].board, position, identifier: id };
  }

  private setCarriedSelection(service: SelectableService<Board>, items: SelectableItem<Board>[]): void {
    this.applyingCarry = true;
    try {
      service.setSelectedItems(items);
    } finally {
      this.applyingCarry = false;
    }
  }

  private dropPendingCarry(): void {
    this.pendingCarry?.watch.unsubscribe();
    this.pendingCarry = null;
  }

  private scrollToTop(): void {
    this.scrollableContainer()?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
